import React, { useEffect } from 'react';
import { Router } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import { createBrowserHistory } from 'history';
import Routes from './Routes';
import Page from './components/Page';
import { auth } from './firebase';
import { signInWithCustomToken } from 'firebase/auth';
import { updateUiState } from 'redux/slices/uiSlice';
import { getDefaultRoute, buildRedirectParams } from 'utils/getDefaultRoute';

import 'react-lazy-load-image-component/src/effects/blur.css';
import 'aos/dist/aos.css';
import Observer from 'components/Observer';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import useOpenReplay from './hooks/useOpenReplay';
import usePusherSubscriptions from './hooks/usePusherSubscriptions';
import {
  checkSession,
  loginUser,
  logoutUser,
  resetUIState,
  setViewAsMode,
  setViewAsLoading,
  setViewAsEmail,
  setForceLogout,
} from 'redux/slices/authSlice';
import { logErrorThunk } from 'redux/slices/miscSlice';

// Create history object outside of component to prevent recreation on render
const browserHistory = createBrowserHistory();

/**
 * Main Application Component.
 *
 * Responsibilities:
 * - Initializes global providers (DndProvider, Observer).
 * - Sets up routing using custom browser history.
 * - Manages global side effects via custom hooks:
 *   - `useOpenReplay`: Initializes OpenReplay session tracking.
 *   - `usePusherSubscriptions`: Manages real-time updates via Pusher.
 *
 * @component
 * @returns {JSX.Element} The rendered application.
 */
const App = () => {
  const dispatch = useDispatch();
  const showForceLogout = useSelector((state) => state.auth.forceLogout.show);

  useEffect(() => {
    const search = window.location.search;
    const urlParams = new URLSearchParams(search);
    const impersonateToken = urlParams.get('impersonate_token');

    if (impersonateToken) {
      // Remove token from URL to avoid leaking it and re-triggering
      const newUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);

      const redirectTo = urlParams.get('redirectTo');
      const redirectToId = urlParams.get('redirectToId');
      const redirectToURI = urlParams.get('redirectToURI');

      dispatch(setViewAsLoading(true));

      signInWithCustomToken(auth, impersonateToken)
        .then(async (userCreds) => {
          dispatch(setViewAsEmail(userCreds.user.email));
          try {
            const idToken = await userCreds.user.getIdToken();
            const signinResponse = await dispatch(
              loginUser({ idToken }),
            ).unwrap();
            localStorage.setItem('isViewAsMode', 'true');
            dispatch(setViewAsMode(true));
            await dispatch(checkSession()).unwrap();
            dispatch(setViewAsLoading(false));
            if (
              redirectTo !== 'action-plan' &&
              !redirectToURI?.includes('action-plan')
            ) {
              dispatch(updateUiState({ key: 'showLoginToast', value: true }));
            }
            if (redirectTo) {
              if (redirectTo.startsWith('jobs')) {
                if (redirectToId) {
                  browserHistory.push({
                    pathname: `/${redirectTo}`,
                    state: { jobId: redirectToId },
                  });
                } else {
                  browserHistory.push(`/${redirectTo}`);
                }
              } else {
                if (redirectToId) {
                  browserHistory.push(
                    search
                      ? `/${redirectTo}/${redirectToId}${search}&comingFromRedirect=true`
                      : `/${redirectTo}/${redirectToId}?comingFromRedirect=true`,
                  );
                } else {
                  browserHistory.push(`/${redirectTo}?comingFromRedirect=true`);
                }
              }
            } else if (redirectToURI) {
              browserHistory.replace(redirectToURI);
            } else {
              if (signinResponse?.whitelabelDomain) {
                if (signinResponse?.loginCount === 1) {
                  if (
                    window.location.host !== signinResponse?.whitelabelDomain
                  ) {
                    dispatch(logoutUser());
                    dispatch(resetUIState());
                    dispatch(checkSession());
                    window.open(
                      `https://${signinResponse?.whitelabelDomain}/org/settings?t=playbook`,
                    );
                  } else {
                    browserHistory.push(
                      getDefaultRoute(buildRedirectParams(signinResponse)),
                    );
                  }
                } else {
                  if (signinResponse?.user?.slug) {
                    if (
                      window.location.host !== signinResponse?.whitelabelDomain
                    ) {
                      dispatch(logoutUser());
                      dispatch(resetUIState());
                      dispatch(checkSession());
                      window.open(
                        `https://${signinResponse?.whitelabelDomain}/${signinResponse?.user?.slug}?comingFromRedirect=true`,
                      );
                    } else {
                      browserHistory.push(
                        `/${signinResponse?.user?.slug}?comingFromRedirect=true`,
                      );
                    }
                  } else {
                    if (
                      window.location.host !== signinResponse?.whitelabelDomain
                    ) {
                      dispatch(logoutUser());
                      dispatch(resetUIState());
                      dispatch(checkSession());
                      window.open(
                        `https://${signinResponse?.whitelabelDomain}`,
                      );
                    } else {
                      browserHistory.push(
                        getDefaultRoute(buildRedirectParams(signinResponse)),
                      );
                    }
                  }
                }
              } else {
                if (signinResponse?.organization?.type === 'w_org') {
                  browserHistory.push('/customer/settings');
                } else if (signinResponse?.organization?.type === 'demo_org') {
                  if (signinResponse?.loginCount === 1) {
                    dispatch(
                      updateUiState({
                        key: 'showOnboardingDialog',
                        value: true,
                      }),
                    );
                    dispatch(
                      updateUiState({
                        key: 'onBoardingStep',
                        value:
                          signinResponse?.organization?.type === 'demo_org'
                            ? 1
                            : 0,
                      }),
                    );
                    browserHistory.push(
                      getDefaultRoute(buildRedirectParams(signinResponse)),
                    );
                  } else {
                    browserHistory.push(
                      signinResponse?.user?.slug
                        ? `/${signinResponse?.user?.slug}?comingFromRedirect=true`
                        : getDefaultRoute(buildRedirectParams(signinResponse)),
                    );
                  }
                } else {
                  if (signinResponse?.loginCount === 1) {
                    dispatch(
                      updateUiState({
                        key: 'showOnboardingDialog',
                        value: true,
                      }),
                    );
                    dispatch(
                      updateUiState({
                        key: 'onBoardingStep',
                        value:
                          signinResponse?.organization?.type === 'demo_org'
                            ? 1
                            : 0,
                      }),
                    );
                    browserHistory.push(
                      getDefaultRoute(buildRedirectParams(signinResponse)),
                    );
                  } else {
                    browserHistory.push(
                      signinResponse?.user?.slug
                        ? `/${signinResponse?.user?.slug}?comingFromRedirect=true`
                        : getDefaultRoute(buildRedirectParams(signinResponse)),
                    );
                  }
                }
              }
            }
          } catch (err) {
            console.error('Failed impersonation login:', err);
            alert('Unable to sign in as user');
          }
        })
        .catch((error) => {
          dispatch(setViewAsLoading(false));
          dispatch(setViewAsEmail(null));
          dispatch(
            logErrorThunk({
              message: error?.message,
              data: {
                formatted: {
                  errorText: error?.message,
                  errorType: error?.code,
                  severity: 'error',
                },
                raw: error,
              },
            }),
          );
          if (
            error.code === 'auth/wrong-password' ||
            error.code === 'auth/user-not-found'
          ) {
            alert('Incorrect email or password');
          } else if (error.code === 'auth/user-disabled') {
            alert(
              'Your account seems disabled. Please contact your administrator or reach out to support to learn more.',
            );
          } else {
            alert(error.message);
          }
          console.log(error.code);
        });
    }
  }, [dispatch]);

  const handleForceLogoutConfirm = () => {
    dispatch(setForceLogout(false));
    dispatch(logoutUser());
    dispatch(resetUIState());
  };

  // Initialize OpenReplay tracker
  useOpenReplay();

  // Initialize Pusher subscriptions
  usePusherSubscriptions();

  // `Page` owns the MUI ThemeProvider/CssBaseline and must sit ABOVE `Observer`:
  // `Observer` renders the boot loader, and when it was the outer wrapper that
  // loader painted with MUI's default palette (and no themed background) while
  // every later loader used the app palette -- two different spinner colours and
  // a white flash in dark mode on every refresh.
  return (
    <Page>
      <Observer>
        <DndProvider backend={HTML5Backend}>
          <Router history={browserHistory}>
            <Routes />
          </Router>
        </DndProvider>
        <Dialog open={showForceLogout} disableEscapeKeyDown>
          <DialogTitle>
            Your session has been terminated. Please log in again to continue.
          </DialogTitle>
          <DialogActions>
            <Button
              onClick={handleForceLogoutConfirm}
              color="primary"
              autoFocus
            >
              Continue to login
            </Button>
          </DialogActions>
        </Dialog>
      </Observer>
    </Page>
  );
};

export default App;
